import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@ezhard/ui';
import { initGA, trackEvent } from '@/services/analytics';
import './CookieConsent.css';

const STORAGE_KEY = 'ezshift-cookies-accepted';

/**
 * Blocking cookie consent overlay.
 *
 * Renders a fullscreen backdrop + centered card that prevents any
 * interaction with the site until the user accepts cookies.
 * Once accepted, the preference is persisted in localStorage and
 * the overlay is permanently hidden.
 */
function CookieConsent() {
  const { t } = useTranslation('common');

  const [accepted, setAccepted] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleAccept = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // Storage full or blocked — still dismiss the overlay for this session
    }
    // Start analytics now that the user has consented
    initGA();
    trackEvent('cookie_consent_accepted');
    setAccepted(true);
  }, []);

  if (accepted) return null;

  return (
    <div className="cookie-consent-overlay" role="dialog" aria-modal="true" aria-label={t('legal.cookieBanner')}>
      <div className="cookie-consent-card">
        <div className="cookie-consent-icon">
          <span className="material-symbols-rounded" aria-hidden="true">cookie</span>
        </div>

        <h2 className="cookie-consent-title">{t('legal.cookieBanner')}</h2>

        <p className="cookie-consent-text">
          {t('legal.cookieMessage')}{' '}
          <Link to="/info?tab=terms" className="cookie-consent-link">
            {t('legal.termsLink')}
          </Link>
        </p>

        <div className="cookie-consent-actions">
          <Button
            variant="primary"
            onClick={handleAccept}
            id="cookie-accept-btn"
          >
            {t('legal.acceptCookies')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
